import {
  Button,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { offices, type Office } from "../../../api/addressBook.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { debounce } from "../../../utils";
import styles from "../AddressBook.module.css";

function OfficesPage() {
  const navigate = useNavigate();
  const { hasFlag } = useFeatureFlags();
  const isLive = hasFlag("address-book-live");
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayedOffices, setDisplayedOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(searchParams.get("search") || "");
  const companyIdFilter = searchParams.get("companyId") || "";
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const updateURL = useCallback(
    (updates: Record<string, string>, options?: { replace?: boolean }) => {
      const newParams = new URLSearchParams(window.location.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== "") {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, options);
    },
    [setSearchParams],
  );

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: unknown) => {
        const searchValue = String(value);
        setAppliedSearchTerm(prev => {
          if (prev === searchValue) return prev;
          updateURL({ search: searchValue, page: "1" }, { replace: true });
          return searchValue;
        });
      }, 500),
    [updateURL],
  );

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 10;
    const searchFromURL = searchParams.get("search") || "";
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
    if (searchFromURL !== appliedSearchTerm) {
      setSearchTerm(searchFromURL);
      setAppliedSearchTerm(searchFromURL);
    }
  }, [searchParams]);

  useEffect(() => {
    debouncedSetSearch(searchTerm);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchTerm, debouncedSetSearch]);

  useEffect(() => {
    loadOffices();
  }, [currentPage, itemsPerPage, appliedSearchTerm, companyIdFilter]);

  async function loadOffices() {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await offices.getAll({
        search: appliedSearchTerm || undefined,
        companyId: companyIdFilter || undefined,
        limit: itemsPerPage,
        offset: offset,
      });
      setDisplayedOffices(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to load offices");
      setError(error);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  const totalPages = Math.ceil(total / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startIndex = offset;
  const endIndex = offset + displayedOffices.length;

  function handleOfficeClick(office: Office) {
    navigate(ROUTES.addressBook.officeDetail(String(office.id)));
  }

  function handleCreateClick() {
    navigate(ROUTES.addressBook.officeDetail("new"));
  }

  const columns: Column<Office>[] = [
    {
      header: "Company",
      accessor: "companyName",
      render: (value: unknown, row: Office) => {
        const companyName = value as string | null;
        if (!row.companyId || !companyName) return "-";
        return (
          <span
            role="link"
            tabIndex={0}
            style={{ cursor: "pointer", color: "var(--color-primary)" }}
            onClick={e => {
              e.stopPropagation();
              navigate(ROUTES.addressBook.companyDetail(String(row.companyId)));
            }}
            onKeyDown={e => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              e.stopPropagation();
              navigate(ROUTES.addressBook.companyDetail(String(row.companyId)));
            }}
          >
            {companyName}
          </span>
        );
      },
    },
    {
      header: "Firm Ref",
      accessor: "firmRef",
      render: (value: unknown) => (value as string) || "",
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "City",
      accessor: "city",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Country",
      accessor: "countryName",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Phone",
      accessor: "phone",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (value: unknown) => {
        const date = new Date(value as string);
        return date.toLocaleDateString();
      },
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value: unknown, row: Office) => (
        <div style={{ textAlign: "right" }}>
          {isLive && (
            <PermissionGate permission="addressbook.delete">
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={async e => {
                  e.stopPropagation();
                  if (!window.confirm("Are you sure you want to delete this office?")) return;
                  try {
                    await offices.delete(row.id);
                    setToast({ message: "Office deleted successfully", type: "success" });
                    loadOffices();
                  } catch (err) {
                    setToast({
                      message: err instanceof Error ? err.message : "Failed to delete office",
                      type: "error",
                    });
                  }
                }}
              >
                Delete
              </Button>
            </PermissionGate>
          )}
        </div>
      ),
    },
  ];

  // Check for 401 Unauthorized or 403 Forbidden error
  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  if (loading && !hasLoadedOnce) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Offices" subtitle="Manage office locations in the address book" />
        <LoadingState message="Loading offices..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Offices" subtitle="Manage office locations in the address book" />
        <EmptyState
          variant="error"
          title="Failed to load offices"
          message={error.message}
          action={{
            label: "Retry",
            onClick: loadOffices,
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Offices" subtitle="Manage office locations in the address book">
          {isLive && (
            <PermissionGate permission="addressbook.write">
              <Button variant="contained" color="primary" onClick={handleCreateClick}>
                Create Office
              </Button>
            </PermissionGate>
          )}
        </PageHeader>
      </div>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={value => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="Search by name or firm ref"
          onSubmit={e => {
            e.preventDefault();
            setAppliedSearchTerm(searchTerm);
            setCurrentPage(1);
            updateURL({ search: searchTerm, page: "1" }, { replace: true });
          }}
          onClear={() => {
            setSearchTerm("");
            setAppliedSearchTerm("");
            setCurrentPage(1);
            updateURL({ search: "", page: "1" }, { replace: true });
          }}
        />
      </div>

      <div className={styles.controlsSection}>
        <div className={styles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        <DataTable
          data={displayedOffices}
          columns={columns}
          onRowClick={handleOfficeClick}
          rowKey="id"
        />

        {displayedOffices.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="offices"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default OfficesPage;
